import { useEffect, useState, useCallback } from 'react';
import api from '../api';
import { FileText, Download, Search, ExternalLink, DollarSign, CheckCircle2 } from 'lucide-react';
import FactoringInvoiceModal from './FactoringInvoiceModal';

/**
 * Admin view of every signed ETA-671 Apprenticeship Application &
 * Tuition Agreement. Supports search, pagination, inline details, and
 * a one-click "Print / Download" action per row.
 */
const AgreementsPanel = ({ apiBaseUrl }) => {
  const [agreements, setAgreements] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [factoringAgreement, setFactoringAgreement] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await api.get(`/admin/apprenticeship/agreements`, {
        params: search ? { search } : {},
      });
      setAgreements(r.data?.items || []);
      setTotal(r.data?.total || 0);
    } catch (e) {
      setError(e.response?.data?.detail || 'Unable to load agreements.');
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, search]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div data-testid="admin-agreements-section">
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ margin: 0, marginBottom: 6, color: '#0b2545' }}>
          ETA-671 Apprenticeship Agreements &amp; Tuition Contracts
        </h2>
        <p style={{ color: '#64748b', fontSize: '0.9rem', margin: 0 }}>
          Every student's signed USDOL ETA-671 Apprenticeship Application plus the in-line
          Tuition Agreement (with authorization-to-fund, collect-on-default, sell/factor,
          and hold-harmless clauses) is listed here. Click <strong>View / Print</strong> to
          open the generated PDF.
        </p>
      </div>

      {/* Search */}
      <div
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          marginBottom: 14,
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: 10,
          padding: '10px 12px',
          maxWidth: 520,
        }}
      >
        <Search size={16} color="#64748b" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && load()}
          placeholder="Search name, email, program, city…"
          data-testid="agreements-search-input"
          style={{ flex: 1, border: 'none', background: 'transparent', outline: 'none', fontSize: '0.9rem' }}
        />
        <button
          onClick={load}
          data-testid="agreements-search-btn"
          style={{ background: '#0b2545', color: 'white', border: 'none', padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}
        >
          Search
        </button>
      </div>

      <div style={{ marginBottom: 10, fontSize: '0.82rem', color: '#475569' }} data-testid="agreements-total">
        {loading ? 'Loading…' : `${total} signed agreement${total === 1 ? '' : 's'} on file`}
      </div>

      {error && (
        <div className="alert alert-error" data-testid="agreements-error" style={{ marginBottom: 12 }}>
          {error}
        </div>
      )}

      {/* Table */}
      <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 10 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 780, fontSize: '0.88rem' }}>
          <thead>
            <tr style={{ background: '#0b2545', color: 'white' }}>
              <th style={thStyle}>Signed</th>
              <th style={thStyle}>Apprentice</th>
              <th style={thStyle}>Program</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Stated Tuition</th>
              <th style={thStyle}>Location</th>
              <th style={thStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {agreements.length === 0 && !loading ? (
              <tr>
                <td colSpan={6} style={{ padding: 20, textAlign: 'center', color: '#64748b' }}>
                  No signed agreements yet.
                </td>
              </tr>
            ) : (
              agreements.map((a) => (
                <AgreementRow
                  key={a.id}
                  agreement={a}
                  apiBaseUrl={apiBaseUrl}
                  expanded={expanded === a.id}
                  onToggle={() => setExpanded(expanded === a.id ? null : a.id)}
                  onFactor={() => setFactoringAgreement(a)}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
      {factoringAgreement && (
        <FactoringInvoiceModal
          apiBaseUrl={apiBaseUrl}
          agreement={factoringAgreement}
          onClose={() => setFactoringAgreement(null)}
          onSaved={(invoice) => {
            setAgreements((list) =>
              list.map((x) =>
                x.id === factoringAgreement.id ? { ...x, factoring_invoice: invoice } : x
              )
            );
            setFactoringAgreement((prev) => (prev ? { ...prev, factoring_invoice: invoice } : prev));
          }}
        />
      )}
    </div>
  );
};

const AgreementRow = ({ agreement, apiBaseUrl, expanded, onToggle, onFactor }) => {
  const pdfUrl = `${apiBaseUrl}/api/apprenticeship/agreement/${agreement.user_id}/download?program_code=${agreement.program_code}`;
  return (
    <>
      <tr style={{ borderTop: '1px solid #e2e8f0', background: expanded ? '#eff6ff' : 'white' }} data-testid={`agreement-row-${agreement.user_id}`}>
        <td style={tdStyle}>{new Date(agreement.signed_at).toLocaleDateString()}</td>
        <td style={tdStyle}>
          <div style={{ fontWeight: 600, color: '#0b2545' }}>{agreement.full_name}</div>
          <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{agreement.email}</div>
        </td>
        <td style={tdStyle}>{agreement.program_title}</td>
        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700 }}>
          ${Number(agreement.stated_tuition || 0).toLocaleString()}.00
        </td>
        <td style={tdStyle}>
          {agreement.city}, {agreement.state}
        </td>
        <td style={tdStyle}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`agreement-download-${agreement.user_id}`}
              style={{
                background: '#0b2545',
                color: 'white',
                textDecoration: 'none',
                padding: '6px 10px',
                borderRadius: 6,
                fontSize: '0.75rem',
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <Download size={12} /> View / Print
            </a>
            <button
              type="button"
              onClick={onFactor}
              data-testid={`agreement-factor-${agreement.user_id}`}
              title={agreement.factoring_invoice ? 'Regenerate draft invoice in Stripe' : 'Create $2,500 draft invoice in Stripe'}
              style={{
                background: agreement.factoring_invoice ? '#ecfdf5' : '#fef3c7',
                color: agreement.factoring_invoice ? '#047857' : '#92400e',
                border: `1px solid ${agreement.factoring_invoice ? '#86efac' : '#fcd34d'}`,
                padding: '6px 10px',
                borderRadius: 6,
                fontSize: '0.75rem',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              {agreement.factoring_invoice ? (
                <>
                  <CheckCircle2 size={12} /> Factored
                </>
              ) : (
                <>
                  <DollarSign size={12} /> Factor $2,500
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onToggle}
              data-testid={`agreement-toggle-${agreement.user_id}`}
              style={{
                background: '#f1f5f9',
                color: '#0b2545',
                border: '1px solid #cbd5e1',
                padding: '6px 10px',
                borderRadius: 6,
                fontSize: '0.75rem',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {expanded ? 'Hide' : 'Details'}
            </button>
          </div>
        </td>
      </tr>
      {expanded && (
        <tr style={{ background: '#f8fafc' }}>
          <td colSpan={6} style={{ padding: '14px 18px', borderTop: '1px solid #e2e8f0' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12, fontSize: '0.83rem', color: '#0b2545' }}>
              <DetailItem label="Phone" value={agreement.phone} />
              <DetailItem label="Date of Birth" value={agreement.date_of_birth} />
              <DetailItem label="Address" value={`${agreement.address_line1}${agreement.address_line2 ? ', ' + agreement.address_line2 : ''}, ${agreement.city}, ${agreement.state} ${agreement.zip_code}`} />
              <DetailItem label="Typed Signature" value={agreement.typed_signature} />
              <DetailItem label="Client IP" value={agreement.client_ip} />
              <DetailItem label="Signed At (UTC)" value={agreement.signed_at} />
              <DetailItem label="Acknowledgements" value="Terms ✓   Tuition ✓   Factoring ✓   Hold-Harmless ✓   Promise-to-Pay ✓   Survival/Consent ✓" />
              <DetailItem label="Program Code" value={agreement.program_code} />
              {agreement.factoring_invoice && (
                <DetailItem
                  label="Factoring Invoice"
                  value={
                    <span>
                      <code style={{ background: '#f1f5f9', padding: '1px 5px', borderRadius: 3, fontSize: '0.75rem' }}>
                        {agreement.factoring_invoice.invoice_id}
                      </code>
                      {' — '}
                      <strong>{(agreement.factoring_invoice.status || '').toUpperCase()}</strong>
                      {' — '}
                      {agreement.factoring_invoice.partner_name}
                      {agreement.factoring_invoice.dashboard_url && (
                        <>
                          {' '}
                          <a
                            href={agreement.factoring_invoice.dashboard_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#635bff', marginLeft: 4, display: 'inline-flex', alignItems: 'center', gap: 2 }}
                            data-testid={`agreement-stripe-link-${agreement.user_id}`}
                          >
                            Stripe <ExternalLink size={10} />
                          </a>
                        </>
                      )}
                    </span>
                  }
                />
              )}
            </div>
            <div style={{ marginTop: 12, fontSize: '0.78rem', color: '#64748b', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <FileText size={12} />
              SSN/State-ID stored encrypted in the record; masked to last-4 on the generated PDF.
              <a
                href={`${apiBaseUrl}/api/apprenticeship/agreement/${agreement.user_id}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#2563eb', marginLeft: 6, display: 'inline-flex', alignItems: 'center', gap: 2 }}
              >
                Raw JSON <ExternalLink size={10} />
              </a>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const DetailItem = ({ label, value }) => (
  <div>
    <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5, color: '#64748b' }}>{label}</div>
    <div style={{ fontWeight: 500, marginTop: 2, wordBreak: 'break-word' }}>{value || '—'}</div>
  </div>
);

const thStyle = { padding: '10px 12px', textAlign: 'left', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 0.6 };
const tdStyle = { padding: '10px 12px', verticalAlign: 'top' };

export default AgreementsPanel;
